// To call back reads and the "is calling" claim (contract ~/pm/contracts/V2-1.md v1 §1–2).
// Writes return { ok: true, data } · { ok: false, uncertain: true } (no response / 5xx:
// it may have saved) · { ok: false, status, message, data } (refused).
import { callApi } from 'src/utils/apiCall';
import newIdempotencyKey from 'src/utils/idempotencyKey';
import { callsBase } from '../callLogQuery';
import { resultOf } from '../caller/callerActions.api';

export const queueUrl = (practiceId, params) => `${callsBase(practiceId)}queue/?${params.toString()}`;
export const claimUrl = (practiceId) => `${callsBase(practiceId)}queue/claim/`;
export const archiveUrl = (practiceId) => `${callsBase(practiceId)}queue/archive/`;
export const restoreUrl = (practiceId) => `${callsBase(practiceId)}queue/restore/`;
export const expiredRestoreUrl = (practiceId) => `${callsBase(practiceId)}queue/expired/restore/`;
export const releaseUrl = (practiceId) => `${callsBase(practiceId)}queue/claim/release/`;

const WRITE_OPTIONS = { suppressServerErrorDispatch: true, timeout: 15000 };

export async function fetchQueue(practiceId, params, signal) {
  return callApi(queueUrl(practiceId, params), 'GET', null, false, { signal, suppressServerErrorDispatch: true });
}

// Claim the caller before ringing them. 409 = someone else is calling: { conflict, claim }.
export async function claimNumber(practiceId, number, { takeOver = false } = {}) {
  const response = await callApi(claimUrl(practiceId), 'POST', { number, take_over: takeOver }, false, {
    ...WRITE_OPTIONS, headers: { 'Idempotency-Key': newIdempotencyKey() },
  });
  if (response?.status === 409 && response.data?.code === 'claimed_by_other') return { ok: false, conflict: true, claim: response.data.claim || null };
  const result = resultOf(response, 'The call-back could not be claimed.');
  return result.ok ? { ok: true, claim: response.data?.claim || null } : result;
}

export async function releaseClaim(practiceId, number) {
  return resultOf(await callApi(releaseUrl(practiceId), 'POST', { number }, false, {
    ...WRITE_OPTIONS, headers: { 'Idempotency-Key': newIdempotencyKey() },
  }), 'The call-back could not be released.');
}

// Archive / Not a patient for 1–100 queue rows (UX-A §2). The caller owns the
// Idempotency-Key: one per user intent, reused on a retry.
// A 409 `idempotency_key_reused` means this key already went through with another
// body: the first write may have saved, so treat it like "couldn't confirm".
export const writeResultOf = (response, fallback) => (
  response?.status === 409 && response.data?.code === 'idempotency_key_reused' ? { ok: false, uncertain: true } : resultOf(response, fallback)
);

export async function archiveNumbers(practiceId, body, key) {
  return writeResultOf(await callApi(archiveUrl(practiceId), 'POST', body, false, {
    ...WRITE_OPTIONS, headers: { 'Idempotency-Key': key || newIdempotencyKey() },
  }), 'The callers could not be archived.');
}

// Undo and per-row Restore (UX-A §3).
export async function restoreArchived(practiceId, body, key) {
  return writeResultOf(await callApi(restoreUrl(practiceId), 'POST', body, false, {
    ...WRITE_OPTIONS, headers: { 'Idempotency-Key': key || newIdempotencyKey() },
  }), 'The callers could not be restored.');
}

// Expired → back on To call back (CU-869fmcbha). body: { numbers: [...] }.
export async function restoreExpired(practiceId, body, key) {
  return writeResultOf(await callApi(expiredRestoreUrl(practiceId), 'POST', body, false, {
    ...WRITE_OPTIONS, headers: { 'Idempotency-Key': key || newIdempotencyKey() },
  }), 'The callers could not be put back on the list.');
}
